/**
 * Merge Tool (listener)
 * Binds UI events (menu item, modal) with EventScope.
 */

import { disposeEventScope, getEventScope } from "../../core/event-scope/event-scope";
import { Roles } from "../../constants/roles";
import { MergeToolController } from "./merge-tool-handler";
import { MergeToolForm } from "./merge-tool-form";
import type { MergeEntity } from "./merge-tool-api";

export class MergeToolListener {
    public register(controller: MergeToolController, modal: HTMLElement): void {
        disposeEventScope(document, "merge-tool");
        const scope = getEventScope(document, "merge-tool");
        const form = new MergeToolForm(modal);

        scope.delegate("click", `[data-role="${Roles.MASTER_MERGE_OPEN}"]`, (event: Event, element: Element) => {
            event.preventDefault();
            const target = form.getMergeOpenTarget(element);
            if (target) {
                void controller.open(target.entity as MergeEntity, target.id);
            }
        });

        scope.delegate("click", `[data-role="${Roles.CLOSE_MERGE_TOOL_MODAL}"]`, (event: Event) => {
            event.preventDefault();
            controller.close();
        });

        scope.on("click", (event: Event) => {
            const target = form.getEventTarget(event);
            if (target && form.isOverlayClick(target)) {
                controller.close();
            }
        });

        scope.delegate("click", ".merge-tool-candidate-btn", (event: Event, element: Element) => {
            const id = form.getCandidateId(element);
            if (id) controller.selectTarget(id);
        });

        scope.delegate("click", ".merge-tool-field__value", (event: Event, element: Element) => {
            const selection = form.getFieldSelection(element);
            if (selection) controller.selectFieldValue(selection.field, selection.side);
        });

        scope.delegate("click", '[data-role="merge-tool-submit"]', (event: Event) => {
            event.preventDefault();
            void controller.submit();
        });

        // "focusout" (not "blur") because it bubbles, which delegate() relies on.
        scope.delegate("focusout", '[data-role="merge-tool-manual-target-input"]', () => {
            void controller.submitManualTarget();
        });

        scope.delegate("keydown", '[data-role="merge-tool-manual-target-input"]', (event: Event) => {
            if (event instanceof KeyboardEvent && event.key === "Enter") {
                event.preventDefault();
                void controller.submitManualTarget();
            }
        });

        scope.on("keydown", (event: Event) => {
            if (event instanceof KeyboardEvent && event.key === "Escape" && modal.style.display !== "none") {
                controller.close();
            }
        });
    }
}
